import React from 'react'
import {RadioGroupItem} from '@/components/ui/radio-group'
import {Label} from '@/components/ui/label'
import builderConfig from 'hec/builder/model-map'
import PropTypes from 'prop-types'
import {cn} from '@/lib/utils'
import {questionPropType} from './utils'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import {useCarousel} from '@/components/ui/carousel'

const GoalAnswer = ({
    answer,
    answerId,
    selectedValue,
    setSelectedValue,
    questions,
    questionTitle,
    setGoal,
    setQuestions,
    addQuestion
}) => {
    const {scrollNext} = useCarousel()
    const {result, answer: answerValue} = answer
    const resultId = result.id
    const shouldQueryResult = !result?.value

    const {
        data: resultData,
        isLoading: isResultDataLoading,
        error: resultDataError
    } = useFetchBuilderReference({
        model: result.model,
        referenceId: resultId,
        shouldQuery: shouldQueryResult
    })

    const resultContent = result?.value?.data || resultData?.data

    if ((!resultContent || resultDataError || isResultDataLoading) && shouldQueryResult) {
        if (resultDataError) {
            console.error(
                'An error occurred while fetching questionnaire answer result data: ',
                resultDataError
            )
        }
        return null
    }

    const areQuestionsEqual = (questions1, questions2) => {
        if (questions1.length !== questions2.length) return false
        return questions1.every((q, index) => q.question === questions2[index].question)
    }

    const determineGoalOrAnswer = () => {
        if (result.model === builderConfig.learningCenterGoal) {
            const currentIndex = questions.findIndex((q) => q.question === questionTitle)
            const newQuestions = questions.slice(0, currentIndex + 1)
            setGoal(resultContent)
            if (!areQuestionsEqual(questions, newQuestions)) {
                setQuestions(newQuestions)
            }
        } else if (result.model === builderConfig.learningCenterQuestionnaireQuestion) {
            setGoal(null)
            addQuestion(resultContent)
        }
    }

    const handleSelectAnswer = (e, selectedAnswer) => {
        e.preventDefault()
        if (selectedAnswer === selectedValue) {
            scrollNext()
        } else {
            setSelectedValue(selectedAnswer)
            determineGoalOrAnswer(selectedAnswer)
        }
    }

    return (
        <div
            tabIndex="0"
            role="button"
            key={`answer-${answerId}`}
            className={cn(
                'border-solid border border-gray-05 rounded-md w-full sm:w-[80%] px-4 py-8 cursor-pointer mb-4',
                {
                    'border-accent border-2 bg-accent-foreground/15': selectedValue === answer
                }
            )}
            onClick={(e) => {
                handleSelectAnswer(e, answerValue)
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    handleSelectAnswer(e, answerValue)
                }
            }}
        >
            <div className="flex items-center space-x-2">
                <RadioGroupItem
                    value={answerValue}
                    id={`answer-${answerId}`}
                    data-testid="goalAnswer-RadioGroupItem"
                />
                <Label
                    htmlFor={`answer-${answerId}`}
                    className="mt-0.5"
                    data-testid="goalAnswer-Label"
                >
                    {answerValue}
                </Label>
            </div>
        </div>
    )
}

export default GoalAnswer

GoalAnswer.propTypes = {
    answer: PropTypes.object.isRequired,
    answerId: PropTypes.number.isRequired,
    selectedValue: PropTypes.string,
    setSelectedValue: PropTypes.func.isRequired,
    questionTitle: PropTypes.string.isRequired,
    questions: PropTypes.arrayOf(questionPropType),
    goal: PropTypes.object || null,
    setGoal: PropTypes.func.isRequired,
    addQuestion: PropTypes.func.isRequired,
    setQuestions: PropTypes.func.isRequired
}
